/**
 * Pull the image URLs out of a user's saved email signatures so the header can
 * warm them in the cache before compose renders them (OE-159).
 *
 * The old inline version matched `\bhttps?:\/\/\S+` — which runs to the next
 * whitespace and therefore swallows the closing quote of `src="…"` — and then
 * did `url.substring(0, url.length - 1)` with the comment "Remove trailing
 * closing quote". That is correct ONLY when a quote is really there: a URL
 * ending at whitespace, at a tag boundary, or at the end of the string lost a
 * real character, so the preload fetched an address that could never resolve.
 *
 * Strip what is actually trailing instead of assuming one character.
 */

/**
 * Stop at the delimiters that end a URL inside HTML — quotes and angle
 * brackets — rather than running to the next whitespace. `\S+` swallowed
 * `"><img src=…` whole, which is why two signature images used to come back as
 * one unusable string.
 */
const URL_IN_HTML = /\bhttps?:\/\/[^\s"'<>]+/gi;

/**
 * Sentence punctuation that can only follow the address, never end it. Brackets
 * are deliberately NOT here: a URL may legitimately contain them, and a wrong
 * strip is worse than a stray character in a cache warm-up.
 */
const TRAILING_PUNCTUATION = /[.,;:!?]+$/;

/**
 * Signature HTML stores its `src` properly escaped — `?alt=media&amp;token=…`.
 * A browser decodes that when it parses the attribute; a regex over the raw
 * HTML does not, so the preloader was fetching a URL whose query parameter was
 * literally named `amp;token`. Firebase saw no download token and answered 403,
 * which is what painted the broken-image glyph in the header (OE-158/OE-159).
 *
 * Only the handful of entities that can appear inside a URL are decoded, and
 * percent-encoding (`%2F` in Storage object paths) is deliberately untouched.
 */
const HTML_ENTITIES: ReadonlyArray<[RegExp, string]> = [
    [/&amp;/gi, '&'],
    [/&#38;/g, '&'],
    [/&quot;/gi, '"'],
    [/&#39;/g, "'"],
    [/&lt;/gi, '<'],
    [/&gt;/gi, '>'],
];

function decodeEntities(url: string): string {
    return HTML_ENTITIES.reduce((acc, [pattern, char]) => acc.replace(pattern, char), url);
}

export function extractSignatureImageUrls(signatures: ReadonlyArray<string | undefined | null>): string[] {
    const urls = new Set<string>();

    for (const html of signatures) {
        if (!html) continue;
        const matches = html.match(URL_IN_HTML);
        if (!matches) continue;

        for (const raw of matches) {
            const cleaned = decodeEntities(raw).replace(TRAILING_PUNCTUATION, '');
            if (cleaned) urls.add(cleaned);
        }
    }

    return [...urls];
}
